import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api/client';
import type { ComplianceScore } from '@/lib/api/types';
import { epicKeys } from './use-epics';
import { epicRequestKeys } from './use-epic-requests';
import { cloudRunsApi } from '@/lib/api/cloud-runs';
import { mailboxApi, type Job } from '@/lib/api/mailbox';
import type { StartExecutionPayload } from '@mycompbox/shared';

interface AuditResponse {
  data: ComplianceScore;
}

/**
 * Mutation hook to trigger a compliance audit on an epic request.
 * POST /compliance/audit/epic-requests/:id
 * Invalidates epic request queries on success.
 */
export function useAuditEpicRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      api.post<AuditResponse>(`/compliance/audit/epic-requests/${id}`, {}),
    onSuccess: (_data, id) => {
      void queryClient.invalidateQueries({ queryKey: epicRequestKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: epicRequestKeys.detail(id) });
    },
  });
}

/**
 * Mutation hook to trigger a compliance audit on an epic.
 * POST /compliance/audit/epics/:id
 * Invalidates epic queries on success.
 */
export function useAuditEpic() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      api.post<AuditResponse>(`/compliance/audit/epics/${id}`, {}),
    onSuccess: (_data, id) => {
      void queryClient.invalidateQueries({ queryKey: epicKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: epicKeys.detail(id) });
    },
  });
}

/**
 * Input for {@link useRefineEpic}. With a `machineId` the refine job goes to
 * that paired computer (optionally on the person's API key); without one it
 * starts a CompBox cloud workspace run on the person's key.
 */
export interface RefineEpicInput {
  epicId: string;
  notes?: string;
  machineId?: string;
  useAiKey?: boolean;
}

/**
 * Mutation hook to send the latest audit feedback (plus optional notes) back
 * to the planner for an epic (ENG-290). Dispatches a `start_execution` job of
 * kind `refine` — to a paired machine via POST /mailbox/jobs, or to a cloud
 * workspace via POST /cloud-runs. Invalidates the epic on success.
 */
export function useRefineEpic() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ epicId, notes, machineId, useAiKey }: RefineEpicInput): Promise<Job> => {
      const trimmed = notes?.trim() ? notes.trim() : undefined;
      if (machineId) {
        const payload: StartExecutionPayload = {
          kind: 'refine',
          epicId,
          ...(trimmed ? { notes: trimmed } : {}),
          ...(useAiKey ? { useAiKey: true } : {}),
        };
        return mailboxApi.enqueueJob({ machineId, type: 'start_execution', payload });
      }
      const res = await cloudRunsApi.start({ kind: 'refine', epicId, ...(trimmed ? { notes: trimmed } : {}) });
      return res.data;
    },
    onSuccess: (_job, { epicId }) => {
      void queryClient.invalidateQueries({ queryKey: epicKeys.detail(epicId) });
    },
  });
}
